import { makePersisted } from "@solid-primitives/storage";
import { useQueryClient } from "@tanstack/solid-query";
import localforage from "localforage";
import type { ParentComponent } from "solid-js";
import { createContext, useContext } from "solid-js";
import { createStore } from "solid-js/store";
import { isServer } from "solid-js/web";
import { FREELANCER_KEY } from "@/api/query-keys";
import type { FreelancerProfile, FreelancerSearchFilters } from "../types";
import { deserializeFreelanceSearchState } from "../utils/sanitize-search-filters";

export type ResearchType = "new" | "from_offer";

export const MAX_SELECTION = 4;
export const REDIRECT_SELECTION_SNAPSHOT_KEY =
  "freelance-search-selected-snapshot";
const FREELANCE_SEARCH_STORE_KEY = "freelance-search-store";

export type InitialResultContext = {
  researchType: ResearchType | null;
  jobOfferId: string | null;
};

export type SelectedFreelancer = Pick<
  FreelancerProfile,
  "id" | "full_name" | "avatar_url" | "job_title" | "already_applied"
> & {
  /**
   * `search_id` della pagina di risultati da cui la selezione è partita,
   * stampato alla selezione (D3). Assente per i freelancer selezionati dal
   * talent pool e per le entry già persistite prima di questa feature: campo
   * opzionale, nessuna migrazione dello store.
   */
  search_id?: string;
};

export type InitialState = {
  researchType: ResearchType | null;
  jobOfferId: string | null;
  selectedFreelancers: SelectedFreelancer[];
  /**
   * Selezione per il download dei CV anonimi — gemella di `selectedFreelancers`
   * (stesso tetto `MAX_SELECTION`, stessa forma) ma **indipendente**: scaricare
   * i CV di 4 candidati non tocca la selezione della proposta, e viceversa. Le
   * due CTA del dettaglio alimentano due liste separate.
   * Campo opzionale in lettura: le entry persistite prima di questa feature non
   * lo hanno (nessuna migrazione dello store).
   */
  selectedForCv: SelectedFreelancer[];
  /** Filters from the first time the user landed on the result page (for Reset). */
  initialResultFilters: FreelancerSearchFilters | null;
  /** Context when initial filters were stored (new search vs from_offer + jobOfferId). */
  initialResultContext: InitialResultContext | null;
  /**
   * Filters used for the current result view. Updated whenever the result page filters (URL) change.
   * Used to prefill the proposal form when creating a proposal from this search.
   */
  proposalPreFillFilters: FreelancerSearchFilters | null;
};

/**
 * Empty state the store mounts with. When a persisted value exists,
 * `makePersisted` reconciles it over this asynchronously (its own `JSON.parse`
 * hydration) — nothing else seeds the store.
 * Returns a fresh object per call: `createStore` proxies and mutates it, so a
 * shared literal would leak state across mounts.
 */
const buildEmptyState = (): InitialState => ({
  researchType: null,
  jobOfferId: null,
  selectedFreelancers: [],
  selectedForCv: [],
  initialResultFilters: null,
  initialResultContext: null,
  proposalPreFillFilters: null,
});

const createFreelanceSearchStore = () =>
  makePersisted(createStore<InitialState>(buildEmptyState()), {
    storage: isServer ? undefined : localforage,
    name: FREELANCE_SEARCH_STORE_KEY,
    deserialize: deserializeFreelanceSearchState,
  });

const FreelanceSearchContext =
  createContext<ReturnType<typeof createFreelanceSearchStore>>();

export const FreelanceSearchProvider: ParentComponent = (props) => {
  const store = createFreelanceSearchStore();

  return (
    <FreelanceSearchContext.Provider value={store}>
      {props.children}
    </FreelanceSearchContext.Provider>
  );
};

export const useFreelanceSearchStore = () => {
  const store = useContext(FreelanceSearchContext);
  if (!store) {
    throw new Error("FreelanceSearchContext not found");
  }
  return store;
};

export const useFreelanceSearchActions = () => {
  const [, setStore] = useFreelanceSearchStore();
  const queryClient = useQueryClient();

  const setResearchType = (type: ResearchType | null) => {
    setStore("researchType", type);
    // If changing research type, clear jobOfferId if switching from "from_offer" to "new"
    if (type === "new") {
      setStore("jobOfferId", null);
    }
  };

  const setJobOfferId = (jobOfferId: string | null) => {
    setStore("jobOfferId", jobOfferId);
    // If setting a job offer, automatically set research type to "from_offer"
    if (jobOfferId) {
      setStore("researchType", "from_offer");
    }
  };

  const addFreelancer = (freelancer: FreelancerProfile) => {
    setStore("selectedFreelancers", (current) => {
      // Check if already selected
      if (current.some((f) => f.id === freelancer.id)) {
        return current;
      }
      // Check max selection
      if (current.length >= MAX_SELECTION) {
        return current;
      }
      return [...current, freelancer];
    });
  };

  const removeFreelancer = (freelancerId: string) => {
    setStore("selectedFreelancers", (current) =>
      current.filter((f) => f.id !== freelancerId),
    );
  };

  const toggleFreelancer = (freelancer: FreelancerProfile) => {
    setStore("selectedFreelancers", (current) => {
      const isSelected = current.some((f) => f.id === freelancer.id);
      if (isSelected) {
        return current.filter((f) => f.id !== freelancer.id);
      }
      // Check max selection
      if (current.length >= MAX_SELECTION) {
        return current;
      }
      return [...current, freelancer];
    });
  };

  /**
   * Azzera ENTRAMBE le selezioni del risultato: chi si voleva contattare e chi
   * si voleva scaricare in CV anonimo. È il gesto "questa ricerca riparte da
   * zero" — i candidati di prima non c'entrano con la ricerca nuova, e la
   * selezione CV vive nello stesso store persistito, quindi senza questo
   * sarebbe sopravvissuta alla nuova ricerca con la barra ancora in alto.
   */
  const clearSelection = () => {
    setStore("selectedFreelancers", []);
    setStore("selectedForCv", []);
  };

  const clearFreelancers = () => {
    clearSelection();
  };

  const setSelectedFreelancers = (freelancers: SelectedFreelancer[]) => {
    const deduped = freelancers
      .reduce<SelectedFreelancer[]>((acc, freelancer) => {
        const id = String(freelancer.id);
        if (acc.some((item) => String(item.id) === id)) {
          return acc;
        }
        acc.push({
          ...freelancer,
          id,
        });
        return acc;
      }, [])
      .slice(0, MAX_SELECTION);
    setStore("selectedFreelancers", deduped);
  };

  const resetStore = () => {
    setStore({
      researchType: null,
      jobOfferId: null,
      selectedFreelancers: [],
      selectedForCv: [],
      initialResultFilters: null,
      initialResultContext: null,
      proposalPreFillFilters: null,
    });
    queryClient.resetQueries({ queryKey: [FREELANCER_KEY, "search"] });
  };

  /** Store filters as "initial research" when first landing on result page (or when context changes). */
  const seedInitialResultFilters = (
    filters: FreelancerSearchFilters,
    context: InitialResultContext,
  ) => {
    setStore((prev) => {
      const currentContext = prev.initialResultContext;
      const contextChanged =
        !currentContext ||
        currentContext.researchType !== context.researchType ||
        currentContext.jobOfferId !== context.jobOfferId;
      const empty = prev.initialResultFilters == null;
      if (contextChanged || empty) {
        return {
          ...prev,
          initialResultFilters: { ...filters },
          initialResultContext: { ...context },
          proposalPreFillFilters: { ...filters },
        };
      }
      return prev;
    });
  };

  /** Update the filters used for the current result view (for proposal form prefill). Call when result page filters change. */
  const setProposalPreFillFilters = (
    filters: FreelancerSearchFilters | null,
  ) => {
    setStore("proposalPreFillFilters", filters ? { ...filters } : null);
  };

  const clearInitialResultFilters = () => {
    setStore("initialResultFilters", null);
    setStore("initialResultContext", null);
    setStore("proposalPreFillFilters", null);
  };

  return {
    setResearchType,
    setJobOfferId,
    addFreelancer,
    removeFreelancer,
    toggleFreelancer,
    clearSelection,
    clearFreelancers,
    setSelectedFreelancers,
    resetStore,
    seedInitialResultFilters,
    setProposalPreFillFilters,
    clearInitialResultFilters,
  };
};

export const clearFreelanceSearchStore = async () => {
  await localforage.removeItem(FREELANCE_SEARCH_STORE_KEY);
};

/** Fresh free-search state: no offer context, no selection, no stored filters. */
export const buildFreshSearchState = (): InitialState => ({
  researchType: "new",
  jobOfferId: null,
  selectedFreelancers: [],
  selectedForCv: [],
  initialResultFilters: null,
  initialResultContext: null,
  proposalPreFillFilters: null,
});

/**
 * Fully replaces the persisted store value. Callable from routes outside
 * `FreelanceSearchProvider` (e.g. the talent-pool deep entry). Must be awaited
 * before navigating so `SearchLayout` hydrates the new state.
 * The wire format is `makePersisted`'s default: a JSON string.
 */
export const writeFreelanceSearchState = async (
  state: InitialState,
): Promise<void> => {
  await localforage.setItem(FREELANCE_SEARCH_STORE_KEY, JSON.stringify(state));
};

// Helper to check if max selection is reached
export const isMaxSelectionReached = (selectedCount: number): boolean => {
  return selectedCount >= MAX_SELECTION;
};
